import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, cancel, select, takeLatest } from 'redux-saga/effects';

import {
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import { Feature, VorschadenArt, VwgDatenAntrag } from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  getFachschluessel,
  handleLinieFromVorvertrag,
  mapToBffVorvertragsNummer,
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import dayjs from 'dayjs';
import { convertRisikoortToBffAdresse } from '@ps20-frontend/vorschlagsdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';
import { selectExtremwetterschutzActiveValue } from '../../app/shared/selectors/elementar-selbstbehalt.selector';

export function* watchVorschlagdatenChanged(): SagaIterator {
  yield takeLatest(GlobalActions.VORSCHLAGDATEN_REQUESTED, safeSaga(vorschlagdatenSaga, App.VWG));
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>): SagaIterator {
  try {
    const { angebotsIds } = action.payload;
    const { angebotsId }: IAppState = yield select();

    if (
      !angebotsIds.some(angebot => angebot.appId === App.VWG && angebot.angebotsId === angebotsId)
    ) {
      return yield cancel();
    }
    const state: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();

    const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
    const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

    const vorschlagdaten: VwgDatenAntrag = {
      spartenType: state.produkt.spartenType,
      angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
      angebotsId: state.angebotsId,
      jahresbeitrag: state.jahresbeitrag,
      savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
      gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.VWG,
      ),
      savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
      antragsId: state.antragsnummer,
      sicherungsglaeubiger: null,
      hspLfnr: state.produkt.hspLfnr,
      gebaeude: {
        ferienhaus: state.merkmale.ferienhaus,
        holzhaus: state.merkmale.holzhaus,
        dachbelagWeich: state.merkmale.dachbelag,
        flachdach: state.merkmale.flachdach,
        adresse: convertRisikoortToBffAdresse(state.risikoort),
        baujahr: state.gebaeude.baujahr,
        wohnflaecheQM: state.gebaeude.wohnflaeche,
        nebengebaeudeQM:
          state.gebaeude.nebengebaeude.gesamtflaeche === null ||
          state.gebaeude.nebengebaeude.vorhanden === false
            ? null
            : state.gebaeude.nebengebaeude.gesamtflaeche,
        kellerVorhanden: state.merkmale.keller,
        kellerMitWohnflaeche:
          state.merkmale.kellerMitWohnflaeche === null
            ? false
            : state.merkmale.kellerMitWohnflaeche,
        leerstehend: state.merkmale.nichtDauerhaftBewohnt,
        schwimmbad: state.merkmale.schwimmbad,
        solarthermie: state.merkmale.solarthermie,
        photovoltaik: state.merkmale.photovoltaik,
        geothermie: state.merkmale.geothermie,
        denkmalschutz: state.merkmale.denkmalschutz,
        feuerrohbau: state.merkmale.feuerrohbau,
        vorschaedenVorhanden: state.vorschaeden.vorhanden,
        vorschaeden: [
          {
            vorschadenArt: VorschadenArt.ELEMENTAR,
            anzahl: state.vorschaeden.elementarVorschaden.includeInEinreichen
              ? state.vorschaeden.elementarVorschaden.count
              : 0,
            betrachtungszeitraum: 5,
          },
          {
            vorschadenArt: VorschadenArt.FEUER,
            anzahl: state.vorschaeden.feuerVorschaden.includeInEinreichen
              ? state.vorschaeden.feuerVorschaden.count
              : 0,
            betrachtungszeitraum: 5,
          },
          {
            vorschadenArt: VorschadenArt.LEITUNGSWASSER,
            anzahl: state.vorschaeden.leitungswasserVorschaden.includeInEinreichen
              ? state.vorschaeden.leitungswasserVorschaden.count
              : 0,
            betrachtungszeitraum: 5,
          },
          {
            vorschadenArt: VorschadenArt.STURM_HAGEL,
            anzahl: state.vorschaeden.sturmhagelVorschaden.includeInEinreichen
              ? state.vorschaeden.sturmhagelVorschaden.count
              : 0,
            betrachtungszeitraum: 5,
          },
        ],
        gebkategorie: '',
        gebklasse: state.merkmale.gebklasse,
      },
      produkt: {
        beginn: null,
        ablauf: null,
        selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
        produktline: ausgewaehlteProduktlinie.produktName,
        kurztext: ausgewaehlteProduktlinie.kurztext,
        langtext: ausgewaehlteProduktlinie.langtext,
        fachschluessel: getFachschluessel(state.produkt),
      },
      rbdMax: ausgewaehlteProduktlinie.rbdMax,
      rbdAktuell: state.taasInfo.rbdNachlass,
      treuebonus: state.taasInfo.tbNachlass,
      savedRbdNachlass: state.taasInfo.savedRbdNachlass,
      savedTbNachlass: state.taasInfo.savedTbNachlass,
      vorteilsrabatt: state.taasInfo.vorteilsrabatt,
      preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
      vorversicherung: [],
      antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
      laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
      rabattstufe: state.rabattstufe,
      geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
      vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
      vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
      zielmarktAbweichung: false,
      kooperation: state.vertragsoptionen.selectedKooperation,
      elementarfragebogenFileToAnalyzeAvailable: false,
      extremwetterschutzSelbstbeteiligung: selectExtremwetterschutzActiveValue(state),
      zuersZone: state.gisDaten?.zuersGefaehrdungsklasse,
    };

    handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);

    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten),
    );
  } catch (e) {
    PS20Logger.error(App.VWG, e as Error, 'VorschlagdatenSaga.vorschlagdatenSaga()');
  }
}
